import { ButtonUniDriver, InputUniDriver } from '@wix/design-system/dist/testkit/unidriver';
import type { GetOptions, Testkit, UniDriver } from 'ricos-types/testkit';
import { dataHook, RicosPluginToolbarNames } from 'wix-rich-content-common';
import { INSERT_PLUGIN_BUTTONS } from 'wix-rich-content-editor-common';
import type { HtmlTestkit } from 'wix-rich-content-plugin-html/testkit';
import { createHtmlTestkit } from 'wix-rich-content-plugin-html/testkit';
import type { LinkTestkit } from 'wix-rich-content-plugin-link/testkit';
import { createLinkTestkit } from 'wix-rich-content-plugin-link/testkit';

import {
  SOCIAL_EMBED_MODAL,
  SOCIAL_EMBED_MODAL_INPUT,
  SOCIAL_EMBED_MODAL_SUBMIT_BTN,
  TOOLBAR_BUTTON_REPLACE_TO_LINK,
} from './dataHooks';
import { toolbarsToAddIn } from './getAddButtons';

export type LinkPreviewTestkit = Testkit<{
  content: {
    socialEmbed: ReturnType<HtmlTestkit>['content'];
    linkEmbed: ReturnType<LinkTestkit>['content'];
  };
  insertSocialEmbedModal: {
    exists: (options?: GetOptions) => Promise<boolean>;
    urlInput: ReturnType<typeof InputUniDriver>;
    submitButton: ReturnType<typeof ButtonUniDriver>;
  };
  toolbars: {
    createAddButtonToolbars: (getAddButtonToolbarBase: () => UniDriver<Element>) => {
      clickInstagram: () => Promise<void>;
      clickTiktok: () => Promise<void>;
      clickTwitter: () => Promise<void>;
      toolbarsToAddIn: typeof toolbarsToAddIn;
    };
    createPluginToolbar: (
      getPluginToolbarBase: (
        pluginType:
          | typeof RicosPluginToolbarNames.EMBED
          | typeof RicosPluginToolbarNames.LINK_PREVIEW
      ) => UniDriver<Element>
    ) => {
      clickConvertToLink: () => Promise<void>;
      clickDelete: () => Promise<void>;
      exists: () => Promise<boolean>;
    };
  };
}>;

export const createLinkPreviewTestkit: LinkPreviewTestkit = getBase => {
  const getInsertSocialEmbedModal = async (options?: GetOptions) =>
    await getBase().get(dataHook(SOCIAL_EMBED_MODAL), options);

  return {
    content: {
      socialEmbed: createHtmlTestkit(getBase).content,
      linkEmbed: createLinkTestkit(getBase).content,
    },
    insertSocialEmbedModal: {
      exists: async options => {
        try {
          const socialEmbed = await getInsertSocialEmbedModal(options);
          return socialEmbed.exists();
        } catch (error) {
          return false;
        }
      },
      urlInput: InputUniDriver(getBase().$(dataHook(SOCIAL_EMBED_MODAL_INPUT)), getBase()),
      submitButton: ButtonUniDriver(
        getBase().$(dataHook(SOCIAL_EMBED_MODAL_SUBMIT_BTN)),
        getBase()
      ),
    },
    toolbars: {
      createAddButtonToolbars: getAddButtonToolbarBase => ({
        clickInstagram: async () => {
          const instagramButton = await getAddButtonToolbarBase().get(
            dataHook(INSERT_PLUGIN_BUTTONS.INSTAGRAM)
          );
          await instagramButton.click();
        },
        clickTiktok: async () => {
          const tikTokButton = await getAddButtonToolbarBase().get(
            dataHook(INSERT_PLUGIN_BUTTONS.TIKTOK)
          );
          await tikTokButton.click();
        },
        clickTwitter: async () => {
          const twitterButton = await getAddButtonToolbarBase().get(
            dataHook(INSERT_PLUGIN_BUTTONS.TWITTER)
          );
          await twitterButton.click();
        },
        toolbarsToAddIn,
      }),
      createPluginToolbar: getPluginToolbarBase => ({
        clickConvertToLink: async () => {
          const convertButton = await getPluginToolbarBase(RicosPluginToolbarNames.EMBED).get(
            dataHook(TOOLBAR_BUTTON_REPLACE_TO_LINK)
          );
          await convertButton.click();
        },
        clickDelete: async () => {
          const deleteButton = await getPluginToolbarBase(RicosPluginToolbarNames.EMBED).get(
            dataHook('blockButton_delete')
          );
          await deleteButton.click();
        },
        exists: async () => {
          const pluginToolbar = getPluginToolbarBase(RicosPluginToolbarNames.EMBED);
          return pluginToolbar.exists();
        },
      }),
    },
  };
};
